import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
// eslint-disable-next-line no-restricted-imports
import { Accordion, Checkbox, Container, Divider, Form, Header, Icon } from 'semantic-ui-react';
import { getkinderSystemsReports } from '../../../api/firebase/kinderSystems';
import { logError } from '../../../rollbar';
import { LoadingIndicator } from '../../Shared/BusyIndicator';
import { showErrorToast } from '../../Shared/showToast';
import AttendanceLogTable from './AttendanceLogTable';

import './KinderSystems.scss';

const KinderSystems = ({
  location,
  index,
  activeIndex,
  handleAccordionClick,
  locationsToSaveData,
  onLocationsToSaveDataChange,
}) => {
  const { t } = useTranslation();
  const defaultKinderSystemsConfig = {
    agencyID: '',
    clientSoftwareVersion: '',
    isEnabled: false,
    providerID: '',
    providerName: '',
  };
  const [loading, setLoading] = useState(false);
  const [kinderSystems, setKinderSystems] = useState(defaultKinderSystemsConfig);
  const [attendanceReports, setAttendanceReports] = useState([]);
  const [integrationCount, setIntegrationCount] = useState(0);

  useEffect(() => {
    try {
      if (!location) return;

      if (location.integrations) {
        setIntegrationCount(Object.keys(location.integrations).length);
        if (location.integrations.kinderSystems) {
          setKinderSystems(location.integrations.kinderSystems);
        }
      }
      setLoading(true);
      getkinderSystemsReports({
        organizationID: location.organization,
        locationID: location.id,
      }).then((reports) => {
        setLoading(false);
        setAttendanceReports(reports);
      });
    } catch (err) {
      setLoading(false);
      logError('Error while getting kinder systems report data: ', err);
      showErrorToast(
        t('apps.kinderConnect.settings.loadErrorTitle'),
        t('apps.kinderConnect.settings.loadErrorMessage'),
        err
      );
    }
  }, [location, t]);

  const onkinderSystemIsActiveChange = (e, { checked }) => {
    setKinderSystems({
      ...kinderSystems,
      isEnabled: checked,
    });
    onLocationsToSaveDataChange({
      ...locationsToSaveData,
      [location.id]: {
        ...location,
        integrations: {
          kinderSystems: {
            ...kinderSystems,
            isEnabled: checked,
          },
        },
      },
    });
  };

  const onTextChange = (e, { name, value }) => {
    setKinderSystems({
      ...kinderSystems,
      [name]: value,
    });
    onLocationsToSaveDataChange({
      ...locationsToSaveData,
      [location.id]: {
        ...location,
        integrations: {
          kinderSystems: {
            ...kinderSystems,
            [name]: value,
          },
        },
      },
    });
  };

  return (
    <>
      <Accordion.Title active={activeIndex === index} index={index} onClick={handleAccordionClick}>
        <Header as="h5" floated="left">
          <Icon name="dropdown" />
          {location.name}
        </Header>
        <span className="integration-count">{integrationCount}</span>
      </Accordion.Title>
      <Accordion.Content active={activeIndex === index} className="kinderConnect">
        <Form.Input
          fluid
          type="text"
          id="providerName"
          name="providerName"
          value={kinderSystems.providerName ?? ''}
          label={t('apps.kinderConnect.settings.providerNameLabel')}
          placeholder={t('apps.kinderConnect.settings.providerNamePlaceholder')}
          disabled={!kinderSystems.isEnabled}
          required={kinderSystems.isEnabled}
          onChange={onTextChange}
        />
        <Divider />
        <Form.Group widths="equal">
          <Form.Field>
            <Form.Input
              fluid
              type="text"
              id="providerID"
              name="providerID"
              value={kinderSystems.providerID ?? ''}
              label={t('apps.kinderConnect.settings.providerIdLabel')}
              placeholder={t('apps.kinderConnect.settings.providerIdPlaceholder')}
              disabled={!kinderSystems.isEnabled}
              required={kinderSystems.isEnabled}
              onChange={onTextChange}
            />
          </Form.Field>
          <Form.Field>
            <Form.Input
              fluid
              type="text"
              id="agencyID"
              name="agencyID"
              value={kinderSystems.agencyID ?? ''}
              label={t('apps.kinderConnect.settings.agencyIdLabel')}
              placeholder={t('apps.kinderConnect.settings.agencyIdPlaceholder')}
              disabled={!kinderSystems.isEnabled}
              required={kinderSystems.isEnabled}
              onChange={onTextChange}
            />
          </Form.Field>
          <Form.Field>
            <Form.Input
              fluid
              type="text"
              id="clientSoftwareVersion"
              name="clientSoftwareVersion"
              value={kinderSystems.clientSoftwareVersion ?? ''}
              label={t('apps.kinderConnect.settings.softwareVersionLabel')}
              placeholder={t('apps.kinderConnect.settings.softwareVersionPlaceholder')}
              disabled={!kinderSystems.isEnabled}
              required={kinderSystems.isEnabled}
              onChange={onTextChange}
            />
          </Form.Field>
        </Form.Group>
        <Divider />
        <Form.Group widths="equal" className="kinderConnectToggle">
          <Container fluid>{t('apps.kinderConnect.settings.connectSchoolLabel')}</Container>
          <Form.Radio
            fluid
            toggle
            name="kinderSystemEnabled"
            label={
              kinderSystems.isEnabled
                ? t('apps.kinderConnect.settings.activeStatus')
                : t('apps.kinderConnect.settings.inactiveStatus')
            }
            checked={kinderSystems.isEnabled}
            onChange={onkinderSystemIsActiveChange}
            control={Checkbox}
          />
        </Form.Group>
        <Divider />
        <Container fluid>
          <Header as="h5">{t('apps.kinderConnect.settings.attendanceLogHeading')}</Header>
        </Container>
        {loading ? (
          <LoadingIndicator />
        ) : (
          <AttendanceLogTable attendanceReports={attendanceReports} setAttendanceReports={setAttendanceReports} />
        )}
      </Accordion.Content>
    </>
  );
};

export default KinderSystems;
